import React, { useState } from 'react'
import isEmpty from 'lodash-es/isEmpty'
import {
  AutocompleteWrapper,
  DropzoneArea,
  FileObject,
  TranslationDictionary,
  useLocale
} from '@plandi/common'
import Button from '@material-ui/core/Button'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import Accordion from '@material-ui/core/Accordion'
import AccordionSummary from '@material-ui/core/AccordionSummary'
import AccordionDetails from '@material-ui/core/AccordionDetails'
import InputAdornment from '@material-ui/core/InputAdornment'
import MenuItem from '@material-ui/core/MenuItem'
import FormGroup from '@material-ui/core/FormGroup'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Checkbox from '@material-ui/core/Checkbox'
import Chip from '@material-ui/core/Chip'
import { useFormikContext } from 'formik'
import { PerformantTextField } from './perfomant-textfield'

import { useProjectFormStyles } from './project-form.styles'
import { FormValuesProjectRequest } from '../projects.types'
import {
  useQueryProjectCategories,
  useQueryProjectParts
} from '../project-queries'
import { stagedDesign } from '../projects.constants'

type Props = {
  translate?: TranslationDictionary['data']
}

export const ProjectFormOverallData: React.FC<Props> = ({ translate }) => {
  const classes = useProjectFormStyles()
  const [fileFormat, setFileFormat] = useState('')

  const formik = useFormikContext<FormValuesProjectRequest>()

  const { data: categories } = useQueryProjectCategories()

  const { data: subCategories } = useQueryProjectCategories({
    select: (categories) =>
      categories?.find((category) => category.id === formik.values.categoryId)
        ?.subCategories
  })
  const locale = useLocale()
  const { data: parts } = useQueryProjectParts()

  const handleChangeFileFormat = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFileFormat(event.target.value)
  }

  const handleAddFileFormat = () => {
    if (formik.values.fileFormats?.includes(fileFormat)) {
      setFileFormat('')
      return
    }

    if (!fileFormat.trim().length) {
      return
    }

    formik.setFieldValue('fileFormats', [
      ...(formik.values.fileFormats || []),
      fileFormat
    ])

    setFileFormat('')
  }

  const handleChangeCategory = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    formik.handleChange(event)

    formik.setFieldValue('subCategoryId', null)
  }

  const handleAddCover = (files: FileObject[]) => {
    if (files) {
      const [file] = files
      formik.setFieldValue('cover', file)
    }
  }

  const handleDeleteCover = () => {
    formik.setFieldValue('cover', null)
  }

  const handleAddImages = (files: FileObject[]) => {
    if (files) {
      formik.setFieldValue('images', [
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        ...(formik.values.images ?? []),
        ...files
      ])
    }
  }

  const handleDeleteImage = (file: FileObject, index: number) => {
    formik.setFieldValue(
      'images',
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      formik.values.images?.filter((_, idx) => idx !== index)
    )
  }

  return (
    <Accordion className={classes.accordion} elevation={2} defaultExpanded>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="h5">
          {translate?.shop['add-project-page']['common-data']['title']}
        </Typography>
      </AccordionSummary>
      <AccordionDetails className={classes.accordionDetails}>
        <Typography variant="body2" color="primary" gutterBottom>
          {translate?.shop['add-project-page']['common-data']['cover']}
        </Typography>
        <Typography
          variant="body2"
          color="textSecondary"
          style={{ marginBottom: formik.values.cover ? 20 : 0 }}
        >
          {translate?.shop['add-project-page']['common-data']['cover-desc']}
        </Typography>
        <DropzoneArea
          filesLimit={1}
          maxFileSize={5000000}
          dropzoneText={
            translate?.shop['add-project-page']['common-data']['dropzone']
          }
          onAdd={handleAddCover}
          onDelete={handleDeleteCover}
          fileObjects={formik.values.cover}
          error={Boolean(formik.errors.cover)}
          helperText={formik.errors.cover}
          getFileName={(file) =>
            file.file?.name ? file.file?.name : 'Обложка'
          }
          translate={translate}
          locale={locale}
        />
        <Typography
          variant="body2"
          color="primary"
          gutterBottom
          style={{ marginTop: formik.values.cover ? 20 : 0 }}
        >
          {translate?.shop['add-project-page']['common-data']['image']}
        </Typography>
        <Typography
          variant="body2"
          color="textSecondary"
          style={{
            marginBottom:
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              formik?.values.images && formik?.values.images?.length > 8
                ? 20
                : 0
          }}
        >
          {translate?.shop['add-project-page']['common-data']['image-desc']}
        </Typography>
        <DropzoneArea
          filesLimit={9}
          maxFileSize={5000000}
          dropzoneText={
            translate?.shop['add-project-page']['common-data']['dropzone']
          }
          onAdd={handleAddImages}
          onDelete={handleDeleteImage}
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          fileObjects={formik.values.images}
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          error={Boolean(formik.errors.images)}
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          helperText={formik.errors.images}
          getFileName={(file, index) =>
            file.file?.name ? file.file?.name : `Изображение ${index + 1}`
          }
          translate={translate}
          locale={locale}
        />
        <Typography
          variant="body2"
          color="primary"
          gutterBottom
          style={{
            marginTop:
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              formik?.values.images && formik?.values.images?.length ? 20 : 0
          }}
        >
          {translate?.shop['add-project-page']['common-data']['common']}
        </Typography>
        <PerformantTextField
          label={
            translate?.shop['add-project-page']['common-data']['name-project']
          }
          fullWidth
          variant="outlined"
          margin="normal"
          name="name"
          value={formik.values.name}
          helperText={formik.errors.name}
          error={Boolean(formik.errors.name)}
          disabled={formik.isSubmitting}
        />
        <PerformantTextField
          label={
            translate?.shop['add-project-page']['common-data']['cost-project']
          }
          type="text"
          fullWidth
          variant="outlined"
          margin="normal"
          InputProps={{
            endAdornment: (
              <InputAdornment position="start">
                {locale === 'ru' ? '₽' : '$'}
              </InputAdornment>
            )
          }}
          name="cost"
          onChangePar="isFractal"
          value={formik.values.cost ?? ''}
          helperText={formik.errors.cost}
          error={Boolean(formik.errors.cost)}
          disabled={formik.isSubmitting}
        />
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                name="negotiated"
                style={{ fontWeight: 500 }}
                onChange={formik.handleChange}
                checked={formik.values.negotiated ?? false}
                disabled={formik.isSubmitting}
              />
            }
            label={
              translate?.shop['add-project-page']['common-data'][
                'negotiated-price'
              ]
            }
          />
        </FormGroup>
        <PerformantTextField
          label={
            translate?.shop['add-project-page']['common-data']['year-project']
          }
          fullWidth
          variant="outlined"
          type="text"
          margin="normal"
          name="designYear"
          onChangePar="isInteger"
          value={formik.values.designYear ?? ''}
          helperText={formik.errors.designYear}
          error={Boolean(formik.errors.designYear)}
          disabled={formik.isSubmitting}
        />
        <TextField
          label={
            translate?.shop['add-project-page']['common-data'][
              'category-project'
            ]
          }
          fullWidth
          variant="outlined"
          margin="normal"
          select
          name="categoryId"
          onChange={handleChangeCategory}
          value={formik.values.categoryId ?? ''}
          onBlur={formik.handleBlur('categoryId')}
          error={Boolean(formik.errors.categoryId) && formik.touched.categoryId}
          helperText={
            formik.errors.categoryId && formik.touched.categoryId
              ? formik.errors.categoryId
              : ' '
          }
          disabled={formik.isSubmitting}
        >
          {categories?.map((category) => (
            <MenuItem key={category.id} value={category.id}>
              {category.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label={
            translate?.shop['add-project-page']['common-data'][
              'subcategory-project'
            ]
          }
          fullWidth
          variant="outlined"
          margin="normal"
          select
          name="subCategoryId"
          onChange={formik.handleChange}
          value={formik.values.subCategoryId ?? ''}
          onBlur={formik.handleBlur('subCategoryId')}
          error={
            Boolean(formik.errors.subCategoryId) && formik.touched.subCategoryId
          }
          helperText={
            formik.errors.subCategoryId && formik.touched.subCategoryId
              ? formik.errors.subCategoryId
              : ' '
          }
          disabled={formik.isSubmitting || !formik.values.categoryId}
        >
          {(subCategories ?? [])?.map((subCategory) => (
            <MenuItem key={subCategory.id} value={subCategory.id}>
              {subCategory.name}
            </MenuItem>
          ))}
        </TextField>
        <AutocompleteWrapper
          multiple
          freeSolo={false}
          options={parts ?? []}
          translate={translate}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              label={locale === 'ru' ? 'Раздел' : 'Section'}
              margin="normal"
              name="parts"
              onBlur={formik.handleBlur('parts')}
              error={Boolean(formik.errors.parts) && formik.touched.parts}
              helperText={
                formik.errors.parts && formik.touched.parts
                  ? formik.errors.parts
                  : ' '
              }
            />
          )}
          getOptionSelected={(option, value) => option.name === value.name}
          disabled={formik.isSubmitting}
          getOptionLabel={(option) => option?.name ?? ''}
          // value={formik.values.parts}
          value={formik.values.parts}
          onChange={(_, parts) => formik.setFieldValue('parts', parts)}
        />
        <TextField
          label={
            translate?.shop['add-project-page']['common-data'][
              'stage-of-the-project'
            ]
          }
          fullWidth
          variant="outlined"
          margin="normal"
          select
          name="projectStaging"
          onChange={formik.handleChange}
          value={formik.values.projectStaging ?? ''}
          onBlur={formik.handleBlur('projectStaging')}
          error={
            Boolean(formik.errors.projectStaging) &&
            formik.touched.projectStaging
          }
          helperText={
            formik.errors.projectStaging && formik.touched.projectStaging
              ? formik.errors.projectStaging
              : ' '
          }
          disabled={formik.isSubmitting}
        >
          {stagedDesign(locale).map(([key, value]) => (
            <MenuItem key={key} value={key}>
              {value}
            </MenuItem>
          ))}
        </TextField>
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                name="expertise"
                onChange={formik.handleChange}
                checked={formik.values.expertise ?? false}
                disabled={formik.isSubmitting}
              />
            }
            label={
              translate?.shop['add-project-page']['common-data'][
                'exist-expertise'
              ]
            }
          />
        </FormGroup>
        <TextField
          label={locale === 'ru' ? 'Формат файлов' : 'File format'}
          placeholder={translate?.['start-write']}
          variant="outlined"
          fullWidth
          margin="normal"
          name="fileFormats"
          onChange={handleChangeFileFormat}
          value={fileFormat}
          helperText={
            Array.isArray(formik.errors.fileFormats)
              ? formik.errors.fileFormats?.join('\n')
              : formik.errors.fileFormats
          }
          FormHelperTextProps={{
            classes: {
              root: classes.fileFormatHelperText
            }
          }}
          error={Boolean(formik.errors.fileFormats)}
          disabled={formik.isSubmitting}
        />
        {!isEmpty(formik.values.fileFormats) && (
          <div className={classes.groupChip}>
            {formik.values.fileFormats?.map((fileFormat) => (
              <Chip
                className={classes.chip}
                key={fileFormat}
                label={fileFormat}
                onDelete={() =>
                  formik.setFieldValue(
                    'fileFormats',
                    formik.values.fileFormats?.filter(
                      (fileToDelete) => fileToDelete !== fileFormat
                    )
                  )
                }
                color="primary"
              />
            ))}
          </div>
        )}
        <div className={classes.groupButton}>
          <Button
            type="button"
            variant="contained"
            color="secondary"
            onClick={handleAddFileFormat}
          >
            {translate?.['add-btn']}
          </Button>
        </div>
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                name="bimModel"
                onChange={formik.handleChange}
                checked={formik.values.bimModel ?? false}
                disabled={formik.isSubmitting}
              />
            }
            label={translate?.models}
          />
        </FormGroup>
      </AccordionDetails>
    </Accordion>
  )
}
